/**
 * Unified Opportunity Selectors — Single Source of Truth
 *
 * Every opportunity/quote view MUST derive its data through these selectors.
 * The canonical dataset is salesOpportunities from AppDataContext.
 */

import type { Opportunity, OpportunityStage } from "./opportunityTypes";
import { isActiveStage, isWonStage, isClosedStage } from "./opportunityTypes";
import type { Job } from "./types";
import {
  getOpportunityDisplayValue,
  getOpportunityRevenueValue,
  getOpportunityRep,
} from "./opportunityRevenueSelectors";

/* ═══════ ENRICHMENT ═══════ */

export interface OpportunityMetrics {
  isOpen: boolean;
  isWon: boolean;
  isLost: boolean;
  isClosed: boolean;
  isActive: boolean;
  isFollowUp: boolean;
  ageDays: number;
  isAging7: boolean;
  isAging14: boolean;
}

export interface EnrichedOpportunity {
  opportunity: Opportunity;
  metrics: OpportunityMetrics;
}

/** Compute derived metrics for a single opportunity */
export function deriveOpportunityMetrics(opp: Opportunity): OpportunityMetrics {
  const isWon = isWonStage(opp.stage);
  const isClosed = isClosedStage(opp.stage);
  const isLost = opp.stage === "Closed Lost";
  const isActive = isActiveStage(opp.stage);
  const isFollowUp = opp.stage === "Follow-Up Needed";
  const isOpen = isActive && !isFollowUp;

  const ageDays = Math.floor(
    (Date.now() - new Date(opp.createdAt).getTime()) / (1000 * 60 * 60 * 24),
  );

  return {
    isOpen,
    isWon,
    isLost,
    isClosed,
    isActive,
    isFollowUp,
    ageDays,
    isAging7: ageDays > 7 && isActive,
    isAging14: ageDays > 14 && isActive,
  };
}

/* ═══════ CORE ENRICHMENT ═══════ */

export function getAllOpportunities(opps: Opportunity[]): EnrichedOpportunity[] {
  return opps.map((o) => ({
    opportunity: o,
    metrics: deriveOpportunityMetrics(o),
  }));
}

/* ═══════ SELECTORS ═══════ */

export function getActiveOpportunities(all: EnrichedOpportunity[]): EnrichedOpportunity[] {
  return all.filter((e) => e.metrics.isActive);
}

export function getWonOpportunities(all: EnrichedOpportunity[]): EnrichedOpportunity[] {
  return all.filter((e) => e.metrics.isWon);
}

export function getLostOpportunities(all: EnrichedOpportunity[]): EnrichedOpportunity[] {
  return all.filter((e) => e.metrics.isLost);
}

export function getOpenOpportunities(all: EnrichedOpportunity[]): EnrichedOpportunity[] {
  return all.filter((e) => e.metrics.isOpen);
}

export function getFollowUpOpportunities(all: EnrichedOpportunity[]): EnrichedOpportunity[] {
  return all.filter((e) => e.metrics.isFollowUp);
}

export function getPipelineOpportunities(all: EnrichedOpportunity[]): EnrichedOpportunity[] {
  return all.filter((e) => e.metrics.isActive);
}

export function getRepOpportunities(all: EnrichedOpportunity[], repName: string): EnrichedOpportunity[] {
  return all.filter((e) => e.opportunity.assignedRep === repName);
}

export function getAgingOpportunities(all: EnrichedOpportunity[], days: number): EnrichedOpportunity[] {
  return all.filter((e) => e.metrics.ageDays > days && e.metrics.isActive);
}

/**
 * Compute pipeline-level stats from enriched opportunities.
 * `jobs` is optional for backward compatibility — when provided, pipelineValue
 * resolves through the canonical opportunity value resolver (linked-job-aware).
 */
export function getOpportunityStats(all: EnrichedOpportunity[], jobs: Job[] = []) {
  const open = all.filter((e) => e.metrics.isOpen);
  const followUp = all.filter((e) => e.metrics.isFollowUp);
  const won = all.filter((e) => e.metrics.isWon);
  const lost = all.filter((e) => e.metrics.isLost);
  const closed = [...won, ...lost];
  const aging7 = all.filter((e) => e.metrics.isAging7).length;
  const aging14 = all.filter((e) => e.metrics.isAging14).length;
  const pipelineValue = [...open, ...followUp].reduce(
    (s, e) => s + getOpportunityDisplayValue(e.opportunity, jobs), 0,
  );
  const closeRate = closed.length > 0 ? Math.round((won.length / closed.length) * 100) : 0;

  return {
    openCount: open.length,
    followUpCount: followUp.length,
    wonCount: won.length,
    lostCount: lost.length,
    aging7,
    aging14,
    closeRate,
    pipelineValue,
  };
}

/** Group opportunities by stage */
export function getOpportunitiesByStage(all: EnrichedOpportunity[]): Record<OpportunityStage, EnrichedOpportunity[]> {
  const result = {} as Record<OpportunityStage, EnrichedOpportunity[]>;
  for (const e of all) {
    const stage = e.opportunity.stage;
    if (!result[stage]) result[stage] = [];
    result[stage].push(e);
  }
  return result;
}

/**
 * Rep-level summary for intelligence reports.
 * Resolves rep identity and revenue through canonical resolvers when `jobs`
 * is supplied — Closed Won deals attach to their linked job's rep and
 * change-order-aware contract value, with realized-revenue rules applied.
 */
export function getRepOpportunitySummary(opps: Opportunity[], jobs: Job[] = []) {
  const repMap = new Map<string, { rep: string; total: number; won: number; lost: number; revenue: number; closed: number }>();

  for (const o of opps) {
    const rep = getOpportunityRep(o, jobs);
    const cur = repMap.get(rep) || { rep, total: 0, won: 0, lost: 0, revenue: 0, closed: 0 };
    cur.total++;
    if (isWonStage(o.stage)) {
      cur.won++;
      cur.revenue += getOpportunityRevenueValue(o, jobs);
      cur.closed++;
    } else if (o.stage === "Closed Lost") {
      cur.lost++;
      cur.closed++;
    }
    repMap.set(rep, cur);
  }

  return [...repMap.values()].map((d) => ({
    ...d,
    closeRate: d.closed > 0 ? Math.round((d.won / d.closed) * 100) : 0,
  })).sort((a, b) => b.revenue - a.revenue);
}
